import { gettext } from '@eventbrite/i18n';
import { DestinationEvent, DiscoveryEvent, FormattedEvent } from '../types';

const getLocationInfo = (venue?: {
    name: string;
    address?: { localized_area_display: string };
}) => {
    //If we have both name and address add comma separated address
    if (venue?.name && venue?.address?.localized_area_display) {
        //ex. Eventbrite • San Francisco CA
        return `${venue?.name} \u2022 ${venue?.address?.localized_area_display}`;
    }

    return venue?.name || venue?.address?.localized_area_display || '';
};

export const transformDiscoveryEvent = (
    event: DiscoveryEvent,
): FormattedEvent => {
    let [startDate, startTime] = event.start.local.split('T');
    let subtitle;

    if (event.is_series_parent) {
        startDate = '';
        subtitle = gettext('MULTIPLE DATES').toString();
    }

    return {
        subtitle,
        id: event.id,
        isFree: event.is_free,
        imageUrl: event?.logo?.url,
        name: event?.name.text,
        isOnlineEvent: event.online_event,
        formattedPriceString: event.is_free ? '' : event.price_range,
        startTime: startTime,
        startDate: event.hide_start_date ? undefined : startDate,
        timezone: event.start.timezone,
        url: event.url,
        locationInfo: getLocationInfo(event?.venue),
        savedByYou: event.bookmarked,
        organizerId: event.organizer_id,
    };
};

export const transformDestinationEvent = (
    event: DestinationEvent,
): FormattedEvent => {
    const fullInstanceCount =
        event?.series?.counts?.current_future || event?.num_children;
    const upcomingRepeatingInstanceCount = fullInstanceCount
        ? fullInstanceCount - 1
        : fullInstanceCount;

    return {
        id: event.id,
        minPrice: {
            currency:
                event?.ticket_availability?.minimum_ticket_price?.currency ||
                '',
            //Default format. Consuming app
            //will pass this directly to the eventcard
            currencyFormat: '¤#,##0.00',
            minPriceValue:
                event?.ticket_availability?.minimum_ticket_price?.value || 0,
        },
        isSoldOut:
            event?.event_sales_status?.sales_status === 'sold_out' &&
            !event?.series_id,
        eventSalesStatus: {
            //Default to on_sale in cases where expansion fails or data is not present
            salesStatus: event?.event_sales_status?.sales_status || 'on_sale',
            message: event?.event_sales_status?.message || '',
            messageType: event?.event_sales_status?.message_type || '',
            messageCode: event?.event_sales_status?.message_code || '',
        },
        imageUrl: event?.image?.url || '',
        edgeColor: event?.image?.edge_color || undefined,
        locationInfo: getLocationInfo(event?.primary_venue),
        startTime:
            event?.series?.next_dates?.[0]?.start.split('T')[1] ||
            event.start_time,
        startDate: event.hide_start_date
            ? undefined
            : event?.series?.next_dates?.[0]?.start.split('T')[0] ||
              event.start_date,
        isFree: !!event?.ticket_availability?.is_free,
        ticketsBy: event.tickets_by,
        name: event.name,
        savedByYou: !!event.saves?.saved_by_you,
        url: event.url,
        repeatingInstanceCount: upcomingRepeatingInstanceCount,
        isOnlineEvent: event.is_online_event,
        timezone: event.timezone,
        organizerId: event.primary_organizer_id,
        organizerName: event?.primary_organizer?.name || '',
        organizerFollowerCount:
            event?.primary_organizer?.num_followers || undefined,
        publicCollections: event?.public_collections?.creator_collections
            ? {
                  creatorCollections:
                      event.public_collections.creator_collections,
              }
            : undefined,
    };
};
